import React, { useState, useEffect, useRef } from "react";
import "../assets/FlowTimer.css";

const Timer = ({  initialTime, 
                  isRunning, 
                  onEnd, 
                  isFlashing, 
                  timerName, 
                  shouldStopAudio, 
                  reset, 
                  pomodoroEnabled, 
                  initialFocus, 
                  initialRest,
                  brightnessFactor = 1,
                  volume,
                  tone,
                  inSettings,
                  isDeviceOn
                }) => {
  const [displayTime, setDisplayTime] = useState(initialTime); // State for rendering the time
  const timeRef = useRef(initialTime); // Ref for tracking time without causing re-renders
  const audioRef = useRef(null); // Ref for audio control
  const intervalRef = useRef(null); // Ref for interval management
  const [isCountUp, setIsCountUp] = useState(false); // state for counting up

  const getToneSrc = (toneNumber) => {
    switch (toneNumber) {
      case 1: return "/tones/FocusFlo Tone 1 - Upbeat.mp3";
      case 2: return "/tones/FocusFlo Tone 2 - Chime.mp3";
      case 3: return "/tones/FocusFlo Tone 3 - Beep.mp3";
      case 4: return "/tones/FocusFlo Tone 4 - SingingBowl.mp3";
      case 5: return "/tones/FocusFlo Tone 5 - LofiShort.mp3";
      case 6: return "/tones/FocusFlo Tone 6 - Rain.mp3";
      case 7: return "/tones/FocusFlo Tone 7 - Simple.mp3";
      case 8: return "/tones/FocusFlo Tone 8 - Alert.mp3";
      default: return "/tones/FocusFlo Tone 1 - Upbeat.mp3";
    }
  };

  // Timer countdown logic
  useEffect(() => {
    if (!isRunning) return;
  
    // -- COUNT-UP MODE (if user selected 00:00) --
    if (timeRef.current === 0 || isCountUp) {
      setIsCountUp(true)
      intervalRef.current = setInterval(() => {
        timeRef.current += 1;
        setDisplayTime(timeRef.current);
        console.log(`Counting up: ${timeRef.current}`);
        // No audio is triggered here
      }, 1000);
    }
  
    // -- COUNTDOWN MODE (if user selected > 0) --
    else if (timeRef.current > 0) {
      intervalRef.current = setInterval(() => {
        timeRef.current -= 1;
        setDisplayTime(timeRef.current);
        console.log(`Counting down: ${timeRef.current}`);

        if (timeRef.current === 0) {
          if (audioRef.current) {
            console.log("Timer reached zero, attempting to play audio...");
            audioRef.current.volume = (volume - 1) * 0.25;
            audioRef.current
              .play()
              .then(() => console.log("Audio played successfully."))
              .catch((err) => console.error("Audio playback failed:", err));
          }
        }
  
        // Only do "timer ended" logic if the timer actually started above 0
        if (timeRef.current <= 0) {
          clearInterval(intervalRef.current);
          
          // If Pomodoro & focus timer
          if (pomodoroEnabled && timerName === "focusTimer") {
            timeRef.current = initialFocus + 1;
            if (audioRef.current) {
              audioRef.current.volume = (volume - 1) * 0.25;
              audioRef.current.play().catch((err) => console.error(err));
            }
          }
          // If Pomodoro & rest timer
          if (pomodoroEnabled && timerName === "restTimer") {
            timeRef.current = initialRest + 1;
            if (audioRef.current) {
              audioRef.current.volume = (volume - 1) * 0.25;
              audioRef.current.play().catch((err) => console.error(err));
            }
          }
          onEnd();
        }
      }, 1000);
    }
  
    return () => clearInterval(intervalRef.current);
  }, [isRunning, onEnd, pomodoroEnabled, timerName, initialFocus, initialRest,volume, isCountUp]);
  
  

  // Reset the timer when the `reset` prop changes
  useEffect(() => {
    if (reset) {
      console.log("Reset triggered. Stopping timer and resetting values.");
      clearInterval(intervalRef.current); // Stop any active interval
      timeRef.current = initialTime; // Reset timeRef
      setDisplayTime(initialTime);  // Reset displayed time
      setIsCountUp(false) // exit countup state if in it
    }
  }, [reset, initialTime]);

  // Sync with initialTime whenever it changes (e.g., timer transitions)
  useEffect(() => {
    console.log(`Timer received new initialTime: ${initialTime}`); // Debugging log
    timeRef.current = initialTime; // Synchronize internal time ref
    setDisplayTime(initialTime);  // Update displayed time
  }, [initialTime]);

  // Stop audio when reset is triggered
  useEffect(() => {
    if (shouldStopAudio && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [shouldStopAudio]);

  

  // Format time to MM:SS
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return { minutes, seconds };
  };

  // Function to get the SVG path for a digit or separator
  const getSVGForDigit = (digit) => {
    if (digit === ":") {
      return `/figures/sep.svg`; // Path for separator
    }
    if (digit === "1s") {
      return `/figures/f1s.svg`; // Path for 100+ indicator
    }
    return `/figures/f${digit}.svg`; // Path for digit SVGs
  };  

  // Function to render the time as SVGs
  const renderTimeAsSVGs = () => {
    const { minutes, seconds } = formatTime(displayTime); // Split minutes and seconds

    let minuteDigits = "";
    if (minutes >= 100) {
      // Handle the case for 100+ minutes by using '1s' for the hundreds place
      minuteDigits = `1s${(minutes - 100).toString().padStart(2, "0")}`;
    } else {
      // For less than 100 minutes, just pad the minutes with zeroes
      minuteDigits = minutes.toString().padStart(2, "0");
    }

    const secondDigits = seconds.toString().padStart(2, "0");

    // Build the complete formatted time
    const formattedTime = `${minuteDigits}:${secondDigits}`;

    // Ensure we split the digits properly, handling '1s' separately
    const timeElements = [];
    for (let i = 0; i < formattedTime.length; i++) {
      if (formattedTime[i] === "1" && formattedTime[i + 1] === "s") {
        // Handle '1s' as a special case for the 100s place
        timeElements.push("1s");
        i++; // Skip the next 's' since we've already handled it
      } else {
        timeElements.push(formattedTime[i]);
      }
    }

    // Render each digit/character as an SVG
    return timeElements.map((char, index) => {
      const className = char === "1s" ? "timerDigit special-1s" : char === ":" ? "timerSeparator" : "timerDigit";
      return (
        <img key={index} src={getSVGForDigit(char)} alt={`digit-${char}`} className={className} />
      );
    });
  };


  return (
    <div 
      className={`timerContainer ${timerName}`}
      style={{
        filter: `brightness(${brightnessFactor})`,
        display: isDeviceOn && !inSettings ? "block" : "none"
      }}
    >
        <div className={`timerDisplay ${isFlashing ? "flashing" : ""}`}>
          {renderTimeAsSVGs()}
        </div>
        <audio ref={audioRef} src={getToneSrc(tone)} loop />
    </div>
  );
};

export default Timer;
